import React from "react";
import { ArrowUp, ArrowDown, Minus, Calendar } from "lucide-react";
import type { ComparisonReportResponse } from "../../types";
import { CARD_BG, PAGE_BG, SHADOW } from "../../config/theme";

interface ComparisonLayoutProps {
  data: ComparisonReportResponse;
  onNewUpload?: () => void;
}

function formatMetricValue(key: string, value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return value;

  switch (key) {
    case "total_sales":
    case "total_spend":
      return num >= 1_000_000
        ? `$${(num / 1_000_000).toFixed(2)}M`
        : num >= 1_000
          ? `$${(num / 1_000).toFixed(1)}K`
          : `$${num.toFixed(2)}`;
    case "average_roas":
      return `${num.toFixed(2)}x`;
    case "cpc":
      return `$${num.toFixed(2)}`;
    case "total_impressions":
    case "total_clicks":
      return num >= 1_000_000
        ? `${(num / 1_000_000).toFixed(1)}M`
        : num >= 1_000
          ? `${(num / 1_000).toFixed(1)}K`
          : num.toLocaleString();
    default:
      return num.toLocaleString();
  }
}

function formatDelta(key: string, delta: string): string {
  const num = parseFloat(delta);
  if (isNaN(num)) return delta;
  const abs = Math.abs(num);
  const sign = num >= 0 ? "+" : "-";

  switch (key) {
    case "total_sales":
    case "total_spend":
      return abs >= 1_000
        ? `${sign}$${(abs / 1_000).toFixed(1)}K`
        : `${sign}$${abs.toFixed(2)}`;
    case "average_roas":
      return `${sign}${abs.toFixed(2)}x`;
    case "cpc":
      return `${sign}$${abs.toFixed(2)}`;
    case "total_impressions":
    case "total_clicks":
      return abs >= 1_000
        ? `${sign}${(abs / 1_000).toFixed(1)}K`
        : `${sign}${abs.toLocaleString()}`;
    default:
      return `${sign}${abs.toLocaleString()}`;
  }
}

export const ComparisonLayout: React.FC<ComparisonLayoutProps> = ({
  data,
  onNewUpload,
}) => {
  const { comparison, current_date_range, prior_date_range, retailer, channel } =
    data;

  return (
    <div className={`min-h-screen ${PAGE_BG} p-6`}>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className={`${CARD_BG} ${SHADOW.card} rounded-xl p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Period-over-Period Comparison
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {retailer.charAt(0).toUpperCase() + retailer.slice(1)} &middot;{" "}
                {channel.charAt(0).toUpperCase() + channel.slice(1)}
              </p>
            </div>
            {onNewUpload && (
              <button
                onClick={onNewUpload}
                className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                New Upload
              </button>
            )}
          </div>

          {/* Date ranges */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              <span className="font-medium">Current:</span> {current_date_range}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/30 rounded-lg px-3 py-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="font-medium">Prior:</span> {prior_date_range}
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className={`${CARD_BG} ${SHADOW.card} rounded-xl overflow-hidden`}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Metric Comparison
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Metric
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Current
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Prior
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Delta
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    % Change
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {comparison.metrics.map((metric) => {
                  const isImproved = metric.direction === "improved";
                  const isDeclined = metric.direction === "declined";

                  const colorClass = isImproved
                    ? "text-green-600 dark:text-green-400"
                    : isDeclined
                      ? "text-red-600 dark:text-red-400"
                      : "text-gray-500 dark:text-gray-400";

                  const bgClass = isImproved
                    ? "bg-green-50/50 dark:bg-green-900/10"
                    : isDeclined
                      ? "bg-red-50/50 dark:bg-red-900/10"
                      : "";

                  const DirectionIcon = isImproved
                    ? ArrowUp
                    : isDeclined
                      ? ArrowDown
                      : Minus;

                  return (
                    <tr
                      key={metric.metric_key}
                      className={`${bgClass} hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors`}
                    >
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {metric.metric_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-sm text-gray-700 dark:text-gray-300">
                        {formatMetricValue(metric.metric_key, metric.current_value)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-sm text-gray-500 dark:text-gray-400">
                        {formatMetricValue(metric.metric_key, metric.prior_value)}
                      </td>
                      <td className={`px-6 py-4 text-right font-mono text-sm ${colorClass}`}>
                        <span className="inline-flex items-center gap-1">
                          <DirectionIcon className="h-3.5 w-3.5" />
                          {formatDelta(metric.metric_key, metric.delta)}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-right font-mono text-sm font-semibold ${colorClass}`}>
                        {metric.pct_change !== null
                          ? `${parseFloat(metric.pct_change) >= 0 ? "+" : ""}${metric.pct_change}%`
                          : "N/A"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
